import React from 'react'
import { useFeatureFlagEnabled, useFeatureFlagVariantKey } from '@posthog/react'
import { POSTHOG_FEATURE_FLAGS } from '../PostHog/flags'
import Icon from '../Icon/index'
import { makeIcon, IconName } from '../OracleCommons/icons'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'

export const DIVINATION_ENTRIES = ['tarot', 'oracle_cards', 'horoscope'] as const

export type DivinationEntry = typeof DIVINATION_ENTRIES[number]

export interface SidebarDivinationButtonsProps {
  onSelect: (entry: DivinationEntry) => void
}

interface DivinationButtonProps {
  entry: DivinationEntry
  iconName: IconName
  label: string
  onSelect: (entry: DivinationEntry) => void
}

const DivinationButton: React.FC<DivinationButtonProps> = ({
  entry, iconName, label, onSelect,
}) => (
  <button
    type="button"
    data-testid={`sidebar-divination-${entry}`}
    className={`sidebar-divination-buttons__button sidebar-divination-buttons__button--${entry}`}
    onClick={() => onSelect(entry)}
  >
    <span className="sidebar-divination-buttons__icon" aria-hidden="true">
      <Icon color="currentColor" size={20}>
        {makeIcon(iconName)}
      </Icon>
    </span>
    <span className="sidebar-divination-buttons__label">{label}</span>
  </button>
)

export const SidebarDivinationButtons: React.FC<SidebarDivinationButtonsProps> = ({
  onSelect,
}) => {
  // Tarot is a MULTIVARIATE experiment, so it gates on the variant KEY rather
  // than useFeatureFlagEnabled — the latter returns truthy for the 'control'
  // arm (the string "control" is truthy), which would leak the experiment to
  // the holdout group (AI-885). Oracle and Horoscope are plain boolean flags.
  const tarotVariant = useFeatureFlagVariantKey(POSTHOG_FEATURE_FLAGS.AI_DIVINATION_TAROT)
  const showTarot = tarotVariant === 'test'
  const oracleCardsFlagEnabled = useFeatureFlagEnabled(
    POSTHOG_FEATURE_FLAGS.AI_DIVINATION_ORACLE_CARDS,
  )
  const horoscopeFlagEnabled = useFeatureFlagEnabled(
    POSTHOG_FEATURE_FLAGS.AI_DIVINATION_HOROSCOPE,
  )

  const tarotLabel = useTranslatedText({ keys: ['sidebarDivination', 'tarot'] })
  const oracleLabel = useTranslatedText({ keys: ['sidebarDivination', 'oracle'] })
  const horoscopeLabel = useTranslatedText({ keys: ['sidebarDivination', 'horoscope'] })

  if (!showTarot && !oracleCardsFlagEnabled && !horoscopeFlagEnabled) {
    return null
  }

  return (
    <div className="sidebar-divination-buttons">
      {showTarot && (
        <DivinationButton
          entry="tarot"
          iconName="tarot-cards"
          label={tarotLabel}
          onSelect={onSelect}
        />
      )}
      {oracleCardsFlagEnabled && (
        <DivinationButton
          entry="oracle_cards"
          iconName="oracle-cards"
          label={oracleLabel}
          onSelect={onSelect}
        />
      )}
      {horoscopeFlagEnabled && (
        <DivinationButton
          entry="horoscope"
          iconName="horoscope"
          label={horoscopeLabel}
          onSelect={onSelect}
        />
      )}
    </div>
  )
}
